class ProgressiveLoader {
  constructor(options = {}) {
    // 默认配置
    this.config = {
      selector: '.lazy-load',     // 图片选择器
      rootMargin: '200px',        // 视口偏移量
      threshold: 0.01,            // 交叉阈值
      loadedClass: 'loaded',      // 加载完成后的 CSS 类名
      usePreloader: true,         // 是否使用 new Image() 预加载
      onLoaded: null,             // 加载成功回调
      onError: null,              // 加载失败回调
      ...options
    };
    this.observer = null;
    this.init();
  }

  init() {
    if (!('IntersectionObserver' in window)) {
      this.loadAllImmediately();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => this._handleIntersection(entries),
      {
        rootMargin: this.config.rootMargin,
        threshold: this.config.threshold,
      }
    );

    this.update(); 
  }

  update() {
    if (!this.observer) return;
    const items = document.querySelectorAll(this.config.selector);
    items.forEach((el) => {
      if (!el.classList.contains(this.config.loadedClass) && !el.dataset.isLoading) {
        this.observer.observe(el);
      }
    });
  }

  _handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const el = entry.target;
        this.observer.unobserve(el); // 立即停止观察
        this._loadImage(el);
      }
    });
  }

  _loadImage(el) {
    if (el.dataset.isLoading) return;
    el.dataset.isLoading = 'true'; 

    if (el.tagName === 'VIDEO') {
      this._handleVideo(el);
      return; 
    }

    const parent = el.parentElement;
    const isPicture = parent && parent.tagName === 'PICTURE';

    const src = el.dataset.src;
    const srcset = el.dataset.srcset;
    const bgSrc = el.dataset.bg; 
    const bgPc = el.dataset.bgPc;       
    const bgMobile = el.dataset.bgMobile; 

    // 如果没有任何资源数据，直接标记完成并退出
    if (!src && !srcset && !bgSrc && !bgPc && !bgMobile) {
      this._applyLoadedClass(el);
      return;
    }

    if (isPicture) {
      const sources = parent.querySelectorAll('source');
      sources.forEach(source => {
        if (source.dataset.srcset) {
          source.srcset = source.dataset.srcset; 
        }
      });
      if (srcset) el.srcset = srcset;
      if (src) el.src = src;
      
      this._applyLoadedClass(el);
      return; 
    }

    // 处理响应式背景图
    if (bgPc || bgMobile) {
      const isMobile = window.innerWidth <= 768; 
      const targetSrc = (isMobile && bgMobile) ? bgMobile : (bgPc || bgMobile);

      const loader = new Image();
      loader.src = targetSrc;
      loader.onload = () => {
        if (bgPc) el.style.setProperty('--bg-desktop', `url(${bgPc})`);
        if (bgMobile) el.style.setProperty('--bg-mobile', `url(${bgMobile})`);
        this._applyLoadedClass(el);
      };
      // 失败也释放锁，防止卡死
      loader.onerror = () => this._applyLoadedClass(el);
      return;
    }

    // 处理简单背景图
    if (bgSrc) {
      const loader = new Image();
      loader.src = bgSrc;
      loader.onload = () => {
        el.style.backgroundImage = `url(${bgSrc})`;
        this._applyLoadedClass(el);
      };
      loader.onerror = () => this._applyLoadedClass(el);
      return;
    }

    // 处理普通图片 (带预加载)
    if (this.config.usePreloader && (src || srcset)) {
      const loader = new Image();
      if (el.sizes) loader.sizes = el.sizes; 
      if (srcset) loader.srcset = srcset;
      if (src) loader.src = src;
      
      loader.onload = () => this._applyAttributes(el, src, srcset);
      loader.onerror = (err) => {
        this._handleError(el, err);
        this._applyAttributes(el, src, srcset);
      };
    } else {
      this._applyAttributes(el, src, srcset);
    }
  }

  _handleVideo(video) {
    const src = video.dataset.src;
    if (!src) {
      this._applyLoadedClass(video);
      return;
    }

    if (!video.querySelector('source')) {
      const source = document.createElement('source');
      source.src = src;
      source.type = 'video/mp4'; 
      video.appendChild(source);
      video.load(); 
    }

    if (video.hasAttribute('autoplay') || video.hasAttribute('loop')) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn("Autoplay was prevented:", error);
        });
      }
    }
    this._applyLoadedClass(video);
  }

  _applyAttributes(el, src, srcset) {
    if (srcset) el.srcset = srcset;
    if (src) el.src = src;
    this._applyLoadedClass(el);
  }

  _applyLoadedClass(el) {
    requestAnimationFrame(() => {
      el.classList.add(this.config.loadedClass);
      delete el.dataset.isLoading; // 释放锁
      if (typeof this.config.onLoaded === 'function') {
        this.config.onLoaded(el);
      }
    });
  }

  _handleError(el, err) {
    console.warn('ProgressiveLoader: Image load failed:', el, err);
    if (typeof this.config.onError === 'function') {
      this.config.onError(el, err);
    }
  }

  loadAllImmediately() {
    const items = document.querySelectorAll(this.config.selector);
    items.forEach(el => this._loadImage(el));
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}